import { type ReactNode } from 'react';
import { ChevronRight, MoreHorizontal } from 'lucide-react';
import { messages } from '@nesc/shared';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { SkeletonRows } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';

export type Column<T> = {
  key: string;
  header: string;
  /** PC の表で表示するか。既定は true */
  visible?: boolean;
  width?: string;
  align?: 'left' | 'right';
  sortable?: boolean;
  /** セル内で折り返す。既定は折り返さず、はみ出す分は各セルの truncate に任せる */
  wrap?: boolean;
  render: (row: T) => ReactNode;
};

export type ResponsiveListProps<T extends { id: string }> = {
  columns: Column<T>[];
  rows: T[];
  /** スマホのカード表示。PC と同じデータ・同じ操作を提供する */
  card: (row: T) => ReactNode;
  selection?: {
    selected: Set<string>;
    onToggle: (id: string) => void;
    onToggleAll: (checked: boolean) => void;
  };
  onRowClick?: (row: T) => void;
  rowActions?: (row: T) => ReactNode;
  sort?: { field: string; direction: 'asc' | 'desc' };
  onSortChange?: (field: string) => void;
  isLoading?: boolean;
  empty?: ReactNode;
  /** さらに読み込む（スマホ）／ページ送り（PC） */
  hasMore?: boolean;
  onLoadMore?: () => void;
  isLoadingMore?: boolean;
  density?: 'comfortable' | 'default' | 'compact';
  /**
   * 列幅の決め方。`fixed` は width 指定を厳密に守り、未指定の列で残りを分け合う。
   * 列が多い一覧で、幅の狭い列の文字が縦に崩れるのを防ぐ。
   */
  layout?: 'auto' | 'fixed';
};

const DENSITY_CLASS = {
  comfortable: 'h-14',
  default: 'h-row',
  compact: 'h-10',
} as const;

/**
 * 一覧（docs/09-design-system.md 5.5）。
 * lg 未満はカード、以上は表を描画する。両者で同じデータと操作を提供し、機能差を作らない。
 * P03：一覧はこの部品を通す。テーブル直書きを禁止する。
 */
export const ResponsiveList = <T extends { id: string }>({
  columns,
  rows,
  card,
  selection,
  onRowClick,
  rowActions,
  sort,
  onSortChange,
  isLoading,
  empty,
  hasMore,
  onLoadMore,
  isLoadingMore,
  density = 'default',
  layout = 'auto',
}: ResponsiveListProps<T>) => {
  const visibleColumns = columns.filter((c) => c.visible !== false);

  if (isLoading) return <SkeletonRows rows={6} />;
  if (rows.length === 0) return <>{empty}</>;

  const allSelected = selection ? rows.every((r) => selection.selected.has(r.id)) : false;

  return (
    <>
      {/* スマホ：カード一覧 */}
      <ul className="space-y-2 lg:hidden">
        {rows.map((row) => (
          <li key={row.id}>
            <Card
              className={cn(
                'min-h-card-row bg-background p-4',
                selection?.selected.has(row.id) && 'border-foreground bg-muted',
              )}
            >
              {card(row)}
            </Card>
          </li>
        ))}
      </ul>

      {/* PC：表 */}
      <div className="hidden overflow-x-auto rounded-card border border-border lg:block">
        <table className={cn('w-full border-collapse text-cell', layout === 'fixed' && 'table-fixed')}>
          <thead className="sticky top-0 z-10 bg-surface">
            <tr className="border-b border-border-strong">
              {selection && (
                <th scope="col" className="w-10 px-3 py-2 text-left">
                  <input
                    type="checkbox"
                    checked={allSelected}
                    onChange={(e) => selection.onToggleAll(e.target.checked)}
                    aria-label={messages.common.selectAll}
                    className="size-4 accent-foreground"
                  />
                </th>
              )}
              {visibleColumns.map((column) => (
                <th
                  key={column.key}
                  scope="col"
                  style={column.width ? { width: column.width } : undefined}
                  className={cn(
                    'whitespace-nowrap px-3 py-2 text-note font-semibold text-foreground-muted',
                    column.align === 'right' ? 'text-right' : 'text-left',
                  )}
                >
                  {column.sortable && onSortChange ? (
                    <button
                      type="button"
                      onClick={() => onSortChange(column.key)}
                      className="inline-flex items-center gap-1 rounded-control px-1 py-0.5 hover:bg-muted"
                      aria-label={`${column.header}で並び替え`}
                    >
                      {column.header}
                      {sort?.field === column.key && (
                        <span aria-hidden className="text-foreground">
                          {sort.direction === 'asc' ? '↑' : '↓'}
                        </span>
                      )}
                    </button>
                  ) : (
                    column.header
                  )}
                </th>
              ))}
              {rowActions && <th scope="col" className="w-12 px-3 py-2" aria-label="操作" />}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr
                key={row.id}
                className={cn(
                  DENSITY_CLASS[density],
                  'border-b border-border transition-colors duration-hover last:border-0',
                  onRowClick && 'cursor-pointer hover:bg-muted',
                  selection?.selected.has(row.id) && 'bg-muted',
                )}
                onClick={onRowClick ? () => onRowClick(row) : undefined}
              >
                {selection && (
                  <td className="px-3" onClick={(e) => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      checked={selection.selected.has(row.id)}
                      onChange={() => selection.onToggle(row.id)}
                      aria-label="この行を選択"
                      className="size-4 accent-foreground"
                    />
                  </td>
                )}
                {visibleColumns.map((column) => (
                  <td
                    key={column.key}
                    className={cn(
                      'px-3 py-1.5',
                      !column.wrap && 'overflow-hidden whitespace-nowrap',
                      column.align === 'right' && 'text-right tabular',
                    )}
                  >
                    {column.render(row)}
                  </td>
                ))}
                {rowActions && (
                  <td className="px-3" onClick={(e) => e.stopPropagation()}>
                    {rowActions(row)}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {hasMore && onLoadMore && (
        <div className="mt-3 flex justify-center">
          <Button variant="outline" size="lg" onClick={onLoadMore} disabled={isLoadingMore}>
            {isLoadingMore ? messages.common.loading : messages.common.loadMore}
          </Button>
        </div>
      )}
    </>
  );
};

/** スマホのカード内で使う操作行（最大 3 つ、それ以上は「…」） */
export const CardActions = ({
  actions,
  className,
}: {
  actions: { label: string; onClick: () => void; icon?: ReactNode; disabled?: boolean }[];
  className?: string;
}) => {
  const visible = actions.slice(0, 3);
  const overflow = actions.slice(3);

  return (
    <div className={cn('mt-3 flex items-center gap-2 border-t border-border pt-3', className)}>
      {visible.map((action) => (
        <Button
          key={action.label}
          variant="outline"
          size="lg"
          className="flex-1"
          onClick={action.onClick}
          disabled={action.disabled}
        >
          {action.icon}
          {action.label}
        </Button>
      ))}
      {overflow.length > 0 && (
        <Button variant="ghost" size="icon-lg" aria-label="その他の操作">
          <MoreHorizontal aria-hidden />
        </Button>
      )}
    </div>
  );
};

/** 詳細への遷移を示す行末のしるし */
export const RowChevron = () => (
  <ChevronRight aria-hidden className="size-4 shrink-0 text-foreground-subtle" />
);
